import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LS_KEY = "pq_recently_viewed";
const MAX_ITEMS = 12;

/** Record a product view. Signed-in users sync to their account (product id +
 * timestamp only); guests stay in localStorage. */
export function recordProductView(productId, user) {
  const ids = JSON.parse(localStorage.getItem(LS_KEY) || "[]").filter((i) => i !== productId);
  ids.unshift(productId);
  localStorage.setItem(LS_KEY, JSON.stringify(ids.slice(0, MAX_ITEMS)));
  if (user) api.post(`/shop/products/${productId}/view`).catch(() => {});
}

function guestCard(p) {
  const enabled = (p.variants || []).filter((v) => v.enabled);
  return {
    product_id: p.id,
    name: p.name,
    image: (p.images || [""])[0],
    price_from: enabled.length ? Math.min(...enabled.map((v) => v.price)) : null,
    in_stock: enabled.some((v) => (v.stock || 0) >= (v.min_order_qty || 1)),
  };
}

export default function RecentlyViewed({ excludeId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (user === undefined) return;
    if (user) {
      api.get("/shop/recently-viewed")
        .then(({ data }) => setItems(data.products))
        .catch(() => setItems([]));
    } else {
      const ids = JSON.parse(localStorage.getItem(LS_KEY) || "[]")
        .filter((i) => i !== excludeId).slice(0, MAX_ITEMS);
      Promise.all(ids.map((id) => api.get(`/shop/products/${id}`).then(({ data }) => guestCard(data)).catch(() => null)))
        .then((list) => setItems(list.filter(Boolean)));
    }
  }, [user, excludeId]);

  const shown = (items || []).filter((p) => p.product_id !== excludeId).slice(0, 8);
  if (!shown.length) return null;

  return (
    <div className="mt-14" data-testid="recently-viewed">
      <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
        <span className="h-px w-10 bg-amber-500" /> Recently Viewed
      </p>
      <div className="-mx-6 mt-4 flex gap-4 overflow-x-auto px-6 pb-2 lg:mx-0 lg:px-0">
        {shown.map((p) => (
          <button key={p.product_id} onClick={() => navigate(`/shop/product/${p.product_id}`)}
            data-testid={`recently-viewed-${p.product_id}`}
            className="w-44 shrink-0 rounded-2xl border border-line bg-surface p-3 text-left transition-colors hover:border-amber-500/25">
            <img src={p.image} alt={p.name} loading="lazy" className="h-24 w-full rounded-xl bg-white object-contain" />
            <p className="mt-2 line-clamp-1 text-xs font-semibold text-1">{p.name}</p>
            <p className="mt-0.5 text-xs font-bold text-amber-400">
              {p.price_from !== null ? `${fmt(p.price_from)} onwards` : "—"}
              {!p.in_stock && <span className="ml-1 text-[10px] font-medium text-3">· out of stock</span>}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
